import { UnidadMedida } from '@/enum/unidad_medida';

/**
 * COTI-635 (8.12/8.13) — Conversión entre la unidad PRINCIPAL ("de cotización") y la unidad
 * SECUNDARIA ("productiva") de un material, por `FactorConversion`.
 *
 * Desde el change `coti-635-necesidad-material-unidad-recurso` este criterio lo usa sólo ET
 * (carga de reales en `input_materiales.vue`). OT resuelve la necesidad y los reales por la
 * unidad del recurso en `necesidad_unidad_recurso.js`, sin ninguna conversión por factor.
 *
 * Son funciones PURAS: reciben objetos planos y números, no leen `props`, `ref` ni stores —
 * mismo patrón que `procesos_precedentes.js` y `margenes_pliego_maquina.js`.
 */

// `CantidadTeorica` es la necesidad congelada por el backend al guardar (ver
// `elemento-proceso-material-base.mapper.ts`): no se recalcula aunque el usuario edite otros
// campos del formulario, a diferencia de `Cantidad`. Se hace fallback a `Cantidad` sólo para
// filas creadas antes de esta columna (todavía sin backfill) o recién agregadas en esta sesión.
// Está expresada en la unidad PRINCIPAL del material (ver 8.12/8.13 más abajo).
export function necesidadTeorica(teorico) {
  return Number(teorico.CantidadTeorica ?? teorico.Cantidad ?? 0);
}

// ─── COTI-635 (8.12/8.13): unidad principal/secundaria y su conversión ───────────────────────
// Centralizados acá para no repetir la lógica de fallback (sin secundaria, sin factor, factor
// 0, abreviatura faltante) en el template ni en cada computed.

/** `FactorConversion` utilizable sólo si es numérico y distinto de 0 — nunca se divide por un
 * factor inválido (`null`/`undefined`/`0`/`NaN`). */
export function factorConversionUtilizable(material) {
  const factor = Number(material?.FactorConversion);
  return Number.isFinite(factor) && factor !== 0 ? factor : null;
}

/** La unidad secundaria de un material sólo es utilizable si tiene código Y un factor de
 * conversión utilizable — sin los dos datos no se inventa ninguna conversión. */
export function unidadSecundariaUtilizable(material) {
  return (
    material?.CodigoUnidadMedidaSecundaria != null && factorConversionUtilizable(material) !== null
  );
}

/** Código de unidad en el que se carga/persiste la cantidad real de un ítem (8.13): la
 * secundaria del material si es utilizable, si no la principal. */
export function resolverCodigoUnidadReal(material) {
  return unidadSecundariaUtilizable(material)
    ? material.CodigoUnidadMedidaSecundaria
    : (material?.CodigoUnidadMedida ?? null);
}

/** Abreviatura de la unidad efectiva en la que se carga la cantidad de un real (8.13), para
 * mostrarla al lado del input.
 *
 * Se resuelve primero desde el material de la fila, que es el dato vigente: si el usuario
 * cambia de material, cambia la unidad. La relación `UnidadMedida` que el backend trae en la
 * propia fila real es el respaldo, y es la que cubre los documentos ya guardados: el `include`
 * de `MaterialesReales` baja `Material: true`, que son sólo los campos escalares del material
 * —sin las relaciones de unidad expandidas—, así que ahí el primer camino no tiene datos.
 *
 * Nunca devuelve `undefined`: sin ningún dato devuelve cadena vacía y el template oculta el
 * sufijo en lugar de escribir basura al lado del número. */
export function abreviaturaUnidadReal(real) {
  const material = real?.Material;
  const unidadDelMaterial = unidadSecundariaUtilizable(material)
    ? material?.UnidadMedidaSecundaria
    : material?.UnidadMedida;
  return unidadDelMaterial?.Abreviatura || real?.UnidadMedida?.Abreviatura || '';
}

/**
 * Unidades de medida que no admiten fracción: no se consume ni se compra media hoja ni medio
 * pliego. Al convertir una cantidad HACIA una de ellas, el resultado se redondea hacia arriba.
 *
 * Es el mismo criterio que ya aplica el motor al calcular hojas (`calcularDetalleHojas` en
 * `libs/cotizador/src/costeo-material.ts` de sispro-erp-server, con el comentario "no se compra
 * una fracción de hoja"), replicado acá para que la grilla no muestre hojas con decimales.
 *
 * Ojo con la diferencia respecto del motor en el caso de PLIEGO: `calcularPliegos` (en
 * `libs/cotizador/src/utils.ts`) devuelve la división sin redondear, porque ahí el pliego es una
 * cantidad intermedia divisible de la cascada de cálculo. Acá, en cambio, el pliego es la unidad
 * productiva en la que el usuario carga y consume material, y por eso se redondea igual que la
 * hoja. Son dos usos distintos del mismo concepto, no una inconsistencia.
 */
const UNIDADES_SIN_FRACCION = Object.freeze([UnidadMedida.HOJA, UnidadMedida.PLIEGO]);

/** Redondeo que depende de la unidad DESTINO de una conversión: hacia arriba cuando el destino
 * es una unidad sin fracción (ver `UNIDADES_SIN_FRACCION`). Las demás no se tocan. */
export function redondearSegunUnidadDestino(valor, codigoUnidadDestino) {
  return UNIDADES_SIN_FRACCION.includes(codigoUnidadDestino) ? Math.ceil(valor) : valor;
}

/** Convierte un valor en unidad PRINCIPAL a la unidad EFECTIVA del real (secundaria si es
 * utilizable, si no queda igual en principal) — usado para la semilla del primer real, que
 * antes de 8.13 arrancaba con la necesidad tal cual, en la escala equivocada, y para expresar
 * la necesidad del teórico en la escala secundaria. Aplica el redondeo por unidad destino. */
export function convertirPrincipalAUnidadReal(valorPrincipal, material) {
  if (!unidadSecundariaUtilizable(material)) return valorPrincipal;
  return redondearSegunUnidadDestino(
    valorPrincipal * factorConversionUtilizable(material),
    material.CodigoUnidadMedidaSecundaria,
  );
}

// 3 decimales: misma precisión que ya usa el componente para cantidades de material (`Kilos` y
// `CantidadMaterial` en `NUMBER_META`, más arriba) — alcanza para no arrastrar ruido de punto
// flotante de las conversiones por `FactorConversion` sin truncar de más.
export const PRECISION_CANTIDAD_DUAL = 3;
export function redondearCantidadDual(valor) {
  return Number(Number(valor || 0).toFixed(PRECISION_CANTIDAD_DUAL));
}
